<script setup lang="ts">
    import { onBeforeMount, ref } from 'vue';
    import { getDashboardsList } from '../services/api/dashboard/dashboardApi';
    import { DashboardList } from '../services/api/dashboard/types';
    import { __ } from '../stores/expressions';
    import LoadingDots from './LoadingDots.vue';
    import EchoButton from './EchoButton.vue';
    import { BookmarkIcon, PlusIcon } from '@lucide/vue';

    defineProps<{
        submitLoading?: boolean;
    }>();

    defineEmits<{
        (e: 'submit', value: string): void;
    }>();

    const dashboards = ref<DashboardList>([]);
    const dashboardsLoading = ref(false);
    const dashboardsError = ref(false);
    const selectedDashboard = ref<string | null>(null);

    onBeforeMount(async () => {
        dashboardsLoading.value = true;
        dashboardsError.value = false;
        try {
            const response = await getDashboardsList('Owner');
            dashboards.value = response.dashboards;
        } catch (error) {
            dashboardsError.value = true;
            console.error('Error fetching owner dashboards:', error);
        } finally {
            dashboardsLoading.value = false;
        }
    });
</script>
<template>
    <form @submit.prevent="selectedDashboard && $emit('submit', selectedDashboard)">
        <h2 class="text-lg mb-1 font-semibold">
            {{ __('choose-dashboard-header') }}
        </h2>
        <p class="mb-4">
            {{ __('choose-dashboard-description') }}
        </p>

        <div v-if="dashboardsLoading" class="w-full h-10 flex items-center justify-center mt-12">
            <LoadingDots />
        </div>
        <p v-else-if="dashboardsError" class="text-body mt-10 text-error">
            {{ __('choose-dashboard-error') }}
        </p>
        <p v-else-if="dashboards.length === 0" class="text-body mt-10">
            {{ __('choose-dashboard-empty') }}
        </p>
        <div v-else class="grid grid-cols-2 gap-4 mt-10">
            <label
                v-for="item in dashboards"
                :key="item.id"
                class="flex px-1 py-1.5 text-white relative rounded-md text-sm items-center cursor-pointer"
                :class="selectedDashboard === item.id ? 'bg-primary-beet' : 'bg-gray-dark'"
            >
                <BookmarkIcon class="size-8 mr-1" />
                <input
                    v-model="selectedDashboard"
                    type="radio"
                    name="library"
                    :value="item.id"
                    class="absolute opacity-0"
                />
                {{ item.title }}
            </label>
        </div>
        <div class="flex justify-end mt-12">
            <EchoButton variant="primary" :loading="submitLoading" :disabled="!selectedDashboard">
                {{ __('add') }}
                <template #icon-right><PlusIcon /></template>
            </EchoButton>
        </div>
    </form>
</template>
